import React, { useState, useEffect, useCallback } from "react";
import { Form, Button, Row, Col, Spinner } from "react-bootstrap";
import ApiService from "../../../_services/ApiService";
import CredentialService from "../../../_services/CredentialService";
import PhotoGalleryService from "../../../_services/PhotoGalleryService";

const apiService = ApiService as any;
const credentialService = CredentialService as any;

interface Props {
  selectedPhotoIds: number[];
  onTagsApplied: () => void;
}

interface EntityOption {
  id: number;
  name: string;
}

const TaggingPanel: React.FC<Props> = ({ selectedPhotoIds, onTagsApplied }) => {
  const [schools, setSchools] = useState<EntityOption[]>([]);
  const [programs, setPrograms] = useState<EntityOption[]>([]);
  const [batches, setBatches] = useState<EntityOption[]>([]);
  const [courses, setCourses] = useState<EntityOption[]>([]);
  const [years, setYears] = useState<EntityOption[]>([]);
  const [studentResults, setStudentResults] = useState<EntityOption[]>([]);
  const [studentSearch, setStudentSearch] = useState("");
  const [searchingStudents, setSearchingStudents] = useState(false);

  // Selected tags to apply
  const [selectedTags, setSelectedTags] = useState<
    { entityType: string; entityId: number; label: string }[]
  >([]);
  const [applying, setApplying] = useState(false);

  // Load entity lists on mount
  useEffect(() => {
    loadEntities();
  }, []);

  const loadEntities = async () => {
    try {
      const [schoolsRes, programsRes, yearsRes] = await Promise.all([
        apiService.getAcademicSchools(),
        apiService.getPrograms(),
        apiService.getAcademicYears(),
      ]);
      setSchools(
        (schoolsRes.data?.data || schoolsRes.data || []).map((s: any) => ({
          id: s.Id || s.id || s.schoolId,
          name: s.Name || s.name || s.schoolName,
        })),
      );
      setPrograms(
        (programsRes.data?.programs || programsRes.data?.data || []).map((p: any) => ({
          id: p.Id || p.id || p.programId,
          name: p.Name || p.name || p.programName,
        })),
      );
      setYears(
        (yearsRes.data?.data || []).map((y: any) => ({
          id: y.Id || y.id || y.yearId,
          name: y.Name || y.name || y.yearName,
        })),
      );
    } catch (err) {
      console.error("Failed to load entities", err);
    }
  };

  const handleSearchBatches = async (query: string) => {
    if (!query || query.length < 2) {
      setBatches([]);
      return;
    }
    try {
      const res = await apiService.searchBatches(query);
      setBatches(
        (res.data?.data || res.data || []).map((b: any) => ({
          id: b.Id || b.id || b.batchId,
          name: b.Name || b.name || b.batchName || b.batchCode,
        })),
      );
    } catch (err) {
      console.error("Failed to search batches", err);
    }
  };

  const handleSearchCourses = useCallback(async () => {
    try {
      const res = await apiService.getCourses();
      setCourses(
        (res.data?.data || res.data || []).map((c: any) => ({
          id: c.Id || c.id || c.courseId,
          name: c.Name || c.name || c.courseName,
        })),
      );
    } catch (err) {
      console.error("Failed to load courses", err);
    }
  }, []);

  useEffect(() => {
    handleSearchCourses();
  }, [handleSearchCourses]);

  const handleSearchStudents = async () => {
    if (!studentSearch || studentSearch.length < 2) return;
    setSearchingStudents(true);
    try {
      const res = await credentialService.searchStudents(studentSearch);
      setStudentResults(
        (res.data?.data || res.data || []).map((s: any) => ({
          id: s.enquiryId || s.EnquiryId || s.id,
          name: s.studentName || s.enquiryName || s.name || s.Name,
        })),
      );
    } catch (err) {
      console.error("Failed to search students", err);
    } finally {
      setSearchingStudents(false);
    }
  };

  const addTag = (entityType: string, entityId: number, label: string) => {
    if (selectedTags.some((t) => t.entityType === entityType && t.entityId === entityId)) {
      return; // Already added
    }
    setSelectedTags((prev) => [...prev, { entityType, entityId, label }]);
  };

  const removeTag = (index: number) => {
    setSelectedTags((prev) => prev.filter((_, i) => i !== index));
  };

  const handleApplyTags = async () => {
    if (selectedTags.length === 0 || selectedPhotoIds.length === 0) return;
    setApplying(true);
    try {
      await PhotoGalleryService.tagPhotos(
        selectedPhotoIds,
        selectedTags.map((t) => ({
          entityType: t.entityType,
          entityId: t.entityId,
        })),
      );
      setSelectedTags([]);
      onTagsApplied();
    } catch (err) {
      console.error("Failed to apply tags", err);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="tagging-panel">
      <h6>Tag {selectedPhotoIds.length} selected photo(s)</h6>

      <Row className="g-2 mb-3">
        {/* Student search */}
        <Col md={4}>
          <Form.Label style={{ fontSize: 13 }}>Student</Form.Label>
          <div className="d-flex gap-1">
            <Form.Control
              size="sm"
              placeholder="Name, code, email, mobile..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearchStudents()}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSearchStudents}
              disabled={searchingStudents}
            >
              {searchingStudents ? <Spinner size="sm" /> : "Search"}
            </Button>
          </div>
          {studentResults.length > 0 && (
            <div
              style={{
                maxHeight: 150,
                overflowY: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 4,
                marginTop: 4,
              }}
            >
              {studentResults.map((s) => (
                <div
                  key={s.id}
                  style={{
                    padding: "4px 8px",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                  onClick={() => addTag("STUDENT", s.id, `Student: ${s.name}`)}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </Col>

        {/* Batch search */}
        <Col md={4}>
          <Form.Label style={{ fontSize: 13 }}>Batch</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Search batch..."
            onChange={(e) => handleSearchBatches(e.target.value)}
          />
          {batches.length > 0 && (
            <div
              style={{
                maxHeight: 150,
                overflowY: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 4,
                marginTop: 4,
              }}
            >
              {batches.map((b) => (
                <div
                  key={b.id}
                  style={{
                    padding: "4px 8px",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                  onClick={() => addTag("BATCH", b.id, `Batch: ${b.name}`)}
                >
                  {b.name}
                </div>
              ))}
            </div>
          )}
        </Col>

        {/* School dropdown */}
        <Col md={4}>
          <Form.Label style={{ fontSize: 13 }}>School</Form.Label>
          <Form.Select
            size="sm"
            onChange={(e) => {
              const id = Number(e.target.value);
              if (!id) return;
              const school = schools.find((s) => s.id === id);
              if (school) addTag("SCHOOL", school.id, `School: ${school.name}`);
              e.target.value = "";
            }}
          >
            <option value="">Select school...</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      <Row className="g-2 mb-3">
        {/* Program dropdown */}
        <Col md={4}>
          <Form.Label style={{ fontSize: 13 }}>Program</Form.Label>
          <Form.Select
            size="sm"
            onChange={(e) => {
              const id = Number(e.target.value);
              if (!id) return;
              const prog = programs.find((p) => p.id === id);
              if (prog) addTag("PROGRAM", prog.id, `Program: ${prog.name}`);
              e.target.value = "";
            }}
          >
            <option value="">Select program...</option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Form.Select>
        </Col>

        {/* Course dropdown */}
        <Col md={4}>
          <Form.Label style={{ fontSize: 13 }}>Course</Form.Label>
          <Form.Select
            size="sm"
            onChange={(e) => {
              const id = Number(e.target.value);
              if (!id) return;
              const course = courses.find((c) => c.id === id);
              if (course)
                addTag("COURSE", course.id, `Course: ${course.name}`);
              e.target.value = "";
            }}
          >
            <option value="">Select course...</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Form.Select>
        </Col>

        {/* Year dropdown */}
        <Col md={4}>
          <Form.Label style={{ fontSize: 13 }}>Year</Form.Label>
          <Form.Select
            size="sm"
            onChange={(e) => {
              const id = Number(e.target.value);
              if (!id) return;
              const year = years.find((y) => y.id === id);
              if (year) addTag("YEAR", year.id, `Year: ${year.name}`);
              e.target.value = "";
            }}
          >
            <option value="">Select year...</option>
            {years.map((y) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Selected tags */}
      {selectedTags.length > 0 && (
        <div className="mb-3">
          <Form.Label style={{ fontSize: 13 }}>Tags to apply:</Form.Label>
          <div>
            {selectedTags.map((tag, i) => (
              <span key={i} className="tag-chip">
                {tag.label}
                <span className="remove-tag" onClick={() => removeTag(i)}>
                  x
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      <Button
        variant="primary"
        size="sm"
        onClick={handleApplyTags}
        disabled={
          applying || selectedTags.length === 0 || selectedPhotoIds.length === 0
        }
      >
        {applying ? "Applying..." : `Apply Tags to ${selectedPhotoIds.length} Photo(s)`}
      </Button>
    </div>
  );
};

export default TaggingPanel;
